/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import clsx from 'clsx'
import Head from 'next/head'
// import { useState, useRef, useEffect } from 'react'
import { eventData, eventMapping } from '../../strapi-v4.tracking'
import ImageGridV0Widget from '../image-grid-v0/image-grid-v0'
import { StrapiV4RichTextEditor } from '../rich-text-editor/rich-text-editor'
import StrapiV4Multimedia from '../../atoms-components/multimedia/multimedia'
import { Strapi4CTAByType } from '@/stories/strapi-4/atoms-components/cta-by-type/cta-by-type'
import { getColorHexFromDotBrandColorNotation } from '@/stories/strapi-4/utils/colors'
import { TStrapiV4SectionCardOneWidget } from '@/stories/strapi-4/common-types'
import { strapiLinearGradients } from '@/stories/strapi-4/atoms-components/colors/strapi-linear-gradients'
import Title from '@/stories/strapi-4/common-components/Title'
import colors from '@ind-money/tailwind/tokens/colors'
import { StencilCDNImage } from '@/components/Common/StencilImage'

type TProps = {
  data?: TStrapiV4SectionCardOneWidget
}

export function StrapiV4SectionCardTypeOneWidget(props: TProps) {
  const { data } = props
  // const [viewMore, setViewMore] = useState(false)
  // const contentRef = useRef<HTMLParagraphElement>(null)
  const mweb = !!(typeof window !== 'undefined' && window.innerWidth < 568)

  // useEffect(() => {
  //   if (!contentRef.current) return
  //   const isClamped =
  //     contentRef.current.scrollHeight > contentRef.current.clientHeight
  //   setViewMore(!isClamped)
  // }, [data?.sub_title])

  if (!data) return null

  const { titleHtag, bg_color, title, text_list, heading } = data

  const bgColor = bg_color?.color
    ? getColorHexFromDotBrandColorNotation(bg_color.color)
    : bg_color?.custom_color
      ? bg_color.custom_color
      : colors.brand.white

  const textSectionAlign = data.text_section_align ?? 'left'

  const titleColorGradient = data.title_color?.linear_gradient
    ? strapiLinearGradients[data.title_color.linear_gradient]
    : null

  const headingColorGradient = heading?.color?.linear_gradient
    ? strapiLinearGradients[heading.color.linear_gradient]
    : null

  const subTitle = data.sub_title
  const tagTextData = data.tag_text
  const htmlText = data.rich_text_html?.rich_text_html

  const tagTextColor = tagTextData?.color?.color
    ? getColorHexFromDotBrandColorNotation(tagTextData.color.color)
    : (tagTextData?.color?.custom_color ?? '#ffffff')

  const titleConditionalProps: { id?: string } = {}
  const subtitleConditionalProps: { id?: string } = {}

  if (titleColorGradient || headingColorGradient) {
    titleConditionalProps.id = 'strapi-4-section-one-title'
    subtitleConditionalProps.id = 'strapi-4-section-one-sub-title'
  }

  return (
    <>
      <Head>
        <style
          dangerouslySetInnerHTML={
            headingColorGradient
              ? {
                  __html: `
              #strapi-4-section-one-title {
                background: linear-gradient(${headingColorGradient});
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
              }
              #strapi-4-section-one-sub-title {
                background: linear-gradient(${headingColorGradient});
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
              }
          `,
                }
              : {
                  __html: `
            #strapi-4-section-one-title {
              background: linear-gradient(${titleColorGradient});
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
            #strapi-4-section-one-sub-title {
              background: linear-gradient(${titleColorGradient});
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
        `,
                }
          }
        />
      </Head>
      <div
        style={{
          backgroundColor: bgColor,
        }}
        className={clsx(
          {
            'mb-8 py-8 xl:mb-12 xl:py-12': bgColor !== colors.brand.white,
          },
          'relative overflow-x-hidden',
        )}
      >
        {data.top_right_icon?.navlink && (
          <div className="absolute right-2 top-2 md:right-8 md:top-8">
            <StencilCDNImage
              src={data.top_right_icon.navlink}
              alt={data.top_right_icon.alt}
              height={60}
              width={60}
            />
          </div>
        )}
        <div className="herolike-wrapper py-8 lg:px-6 xl:py-12">
          <div
            className={clsx('relative flex flex-col items-center gap-10', {
              'lg:flex-row lg:justify-between': textSectionAlign === 'left',
              'lg:flex-row-reverse': textSectionAlign === 'right',
              '!text-brand-white': bgColor === '#191C1F',
            })}
          >
            <div className="relative w-full flex-grow lg:w-6/10">
              <div
                className={clsx({
                  'ml-auto': textSectionAlign === 'right',
                  'xl:max-w-[640px]':
                    data.multimedia ?? data.image_grid?.api_path,
                })}
              >
                <div>
                  {tagTextData?.text && (
                    <p
                      className="mb-4 w-fit rounded-sm px-4 py-1"
                      style={{
                        border: `0.5px solid ${tagTextColor}`,
                        color: tagTextColor,
                      }}
                    >
                      {tagTextData.text}
                    </p>
                  )}

                  {heading?.text ? (
                    <Title
                      {...heading}
                      fontSize={heading.fontSize ?? 'size-78'}
                      className={clsx(
                        'pb-3 font-semibold text-brand-black xl:pb-7',
                        {
                          '!text-brand-white': bgColor === '#191C1F',
                        },
                      )}
                      {...titleConditionalProps}
                    >
                      {heading.text}
                    </Title>
                  ) : (
                    title && (
                      <Title
                        fontSize="size-78"
                        headingType={titleHtag}
                        className={clsx(
                          'pb-3 font-semibold text-brand-black xl:pb-7',
                          {
                            '!text-brand-white': bgColor === '#191C1F',
                          },
                        )}
                        {...titleConditionalProps}
                      >
                        {title}
                      </Title>
                    )
                  )}

                  {htmlText && (
                    <StrapiV4RichTextEditor
                      className="text-brand-black/70 [&>p]:leading-9"
                      content={htmlText}
                    />
                  )}

                  {subTitle && (
                    <>
                      <p
                        // ref={contentRef}
                        {...titleConditionalProps}
                        className={clsx(
                          'pb-4 text-lg text-brand-black/70 xl:pb-9 xl:text-[18px] xl:!leading-[36px]',
                          // { 'line-clamp-2 lg:line-clamp-none': !viewMore },
                          // { 'pb-4': viewMore },
                          {
                            '!text-brand-white': bgColor === '#191C1F',
                          },
                        )}
                      >
                        {subTitle}
                      </p>
                      {/* {!viewMore && (
                        <div
                          onClick={() => setViewMore(true)}
                          className="text-brand-grey-light pb-4 text-lg lg:hidden xl:text-[18px] xl:!leading-[36px]"
                        >
                          Read more
                        </div>
                      )} */}
                    </>
                  )}

                  {!!text_list?.length && (
                    <div className="mb-4 flex w-full flex-col flex-wrap justify-start lg:flex-row">
                      {text_list.map((iconText, ind) => {
                        return (
                          <div
                            key={`${iconText.text}-${ind}`}
                            className="mb-3 flex flex-row items-start justify-start gap-2 lg:w-1/2"
                          >
                            {iconText.icon_link && (
                              <StencilCDNImage
                                src={iconText.icon_link}
                                alt={iconText.text}
                                height={30}
                                width={30}
                              />
                            )}
                            <p className="text-lg font-medium">
                              {iconText.text}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
                <div className="mt-5 flex flex-wrap xl:mt-0">
                  {data.cta_one && (
                    <Strapi4CTAByType
                      url={data.cta_one.url}
                      type={data.cta_one.type}
                      eventData={{
                        ...eventMapping[eventData.strapiWidgetCtaClick],
                        eventLabel: data.cta_one.text,
                        section: data.title,
                      }}
                    >
                      {data.cta_one.text}
                    </Strapi4CTAByType>
                  )}

                  {data.cta_two && (
                    <Strapi4CTAByType
                      url={data.cta_two.url}
                      type={data.cta_two.type}
                      eventData={{
                        ...eventMapping[eventData.strapiWidgetCtaClick],
                        eventLabel: data.cta_two.text,
                        section: data.title,
                      }}
                    >
                      {data.cta_two.text}
                    </Strapi4CTAByType>
                  )}
                </div>
              </div>
            </div>
            {data.isHidden && mweb
              ? null
              : (data.multimedia || data.image_grid?.api_path) && (
                  <div className="flex w-full items-center justify-center lg:w-4/10">
                    {data.multimedia && (
                      <StrapiV4Multimedia data={data.multimedia} />
                    )}
                    {data.image_grid?.api_path && (
                      <ImageGridV0Widget apiUrl={data.image_grid.api_path} />
                    )}
                  </div>
                )}
          </div>
        </div>
      </div>
    </>
  )
}
